import React, { useEffect, useState } from "react";
import DefaultButton from "../../buttons/defaultButton";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import DefaultDatePicker from "../../inputs/defaultDatePicker";
import DefaultSearchSelectInput from "../../inputs/defaultSearchSelectInput";
import DefaultTextarea from "../../inputs/defaultTextarea";
import DynamicTable from "../../table/dynamicTable";
import Loading from "../../skeleton/loading";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { _Priority, convertToLabelValue } from "../../../../utils/common";

import {
  createPurchaseOrder,
  createVendor,
  getCategoryList,
  getPurchaseRequestDetail,
  getStockList,
  getVendorList,
  updatePurchaseOrder,
} from "../../../../services/workspace/inventory.service";
import moment from "moment";
import CommonModal from "../../modals/commonModel";
import AddVendorForm from "./AddVendorsForm";
import CommonForm from "../../form/form";
import useCommonFetch from "../../../../hooks/useCommonFetch";

const AddPurchaseOrderForm = () => {
  const location = useLocation();
  // const { purchaseRequest } = location.state || {};
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [data, setData] = useState();
  const [error, setError] = useState();
  const [purchaseItemData, setPurdhaseItemData] = useState([
    { item: "", unitPrice: 0, quantity: 0, amount: 0 },
  ]);
  const [tableData, setTableData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(null);
  const [purchaseRequest, setPurchaseRequest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState([]);
  const [vendor, setVendor] = useState([]);

  const ModalNames = {
    VENDOR: "VENDOR",
  };

  // Validation
  const validationSchema = Yup.object().shape({
    category: Yup.object().required("Category is required"),
    expected_date: Yup.string()
      .trim()
      .required("Expected Date is required")
      .test("isValid", "Invalid date format, expected YYYY-MM-DD", (value) =>
        moment(value, "YYYY-MM-DD", true).isValid()
      ),
    vendor: Yup.object().required("Vendor is required"),
    priority: Yup.object().required("Priority is required"),
  });

  // Fetch Purchase Request Detail
  const fetchRequestData = () => {
    setLoading(true);
    getPurchaseRequestDetail(id)
      .then((response) => {
        setLoading(false);
        setPurchaseRequest(response?.purchaseReq);
      })
      .catch((error) => {
        setLoading(false);
        toast.error("Failed to fetch Purchase Request data!");
        console.error(error);
      });
  };

  // Fetch stock list for dropdown
  const fetchStockList = () => {
    setLoading(true);
    getStockList({})
      .then((response) => {
        setLoading(false);
        setTableData(convertToLabelValue(response.stock.data) || []);
      })
      .catch((error) => {
        setLoading(false);
        toast.error("Failed to fetch stock list!");
        console.error(error);
      });
  };

  // Fetch categories
  const fetchCategories = () => {
    setLoading(true);
    getCategoryList()
      .then((response) => {
        setLoading(false);
        setCategory(convertToLabelValue(response.category.data));
      })
      .catch((error) => {
        setLoading(false);
        toast.error("Failed to fetch categories!");
        console.error(error);
      });
  };

  // Load purchase request if editing
  useEffect(() => {
    if (id) {
      fetchRequestData();
    }
  }, [id]);

  // Prepare item rows after request & stock loaded
  useEffect(() => {
    if (purchaseRequest?.stockItems && tableData.length) {
      const requestData = purchaseRequest?.stockItems.map((item) => {
        const stockItem = tableData.find((tD) => tD.id === item.stock?.id);
        return {
          item: stockItem,
          unitPrice: 0,
          quantity: item?.quantity || 0,
          amount: 0,
        };
      });
      setPurdhaseItemData(requestData);
    }
  }, [purchaseRequest, tableData]);

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    values,
    touched,
    errors,
  } = useFormik({
    initialValues: {
      category: data?.category || null, // null instead of ""
      expected_date: data?.expected_date || "",
      vendor: data?.vendor || null,
      priority: data?.priority || null,
      note: data?.note || "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      setIsSubmitting(true);

      const itemData = [];
      const errorIndex = [];

      purchaseItemData.forEach((row, index) => {
        if (row.item?.id && row.quantity) {
          itemData.push({
            stock_id: row.item.id,
            unit_price: row.unitPrice,
            quantity: parseInt(row.quantity, 10),
          });
        } else {
          if (!row.item?.id) {
            errorIndex.push({ index, key: "item", msg: "Please select Item" });
          }
          if (!row.quantity) {
            errorIndex.push({
              index,
              key: "quantity",
              msg: "Please enter quantity",
            });
          }
        }
      });

      setError(errorIndex);
      if (errorIndex.length) {
        setIsSubmitting(false);
        return;
      }

      const payload = {
        vendor_id: values.vendor.value,
        expected_date: values.expected_date,
        priority: values.priority.value,
        purchase_request_id: id,
        items: itemData,
        notes: values.note,
      };

      const apiCall = id
        ? updatePurchaseOrder(payload, id)
        : createPurchaseOrder(payload);

      apiCall
        .then(() => {
          toast.success(
            `Purchase Order ${id ? "updated" : "created"} successfully!`
          );
          navigate("/inventory/purchase-order");
        })
        .catch((error) => {
          setIsSubmitting(false);
          toast.error(
            `Failed to ${id ? "update" : "create"} Purchase Order! ${
              error.response?.data?.message || ""
            }`
          );
          console.error(error);
        });
    },
  });

  // Fetch vendors by category
  const getVendorListData = (categoryId = null) => {
    setLoading(true);
    const params = categoryId ? { category_id: categoryId } : {};
    getVendorList(params)
      .then((response) => {
        setLoading(false);
        setVendor(convertToLabelValue(response.vendor.data));
      })
      .catch((error) => {
        setLoading(false);
        toast.error("Failed to fetch vendors!");
        console.error(error);
      });
  };

  // Fetch vendors when category changes
  useEffect(() => {
    if (values?.category?.value) {
      getVendorListData(values.category.value);
    } else {
      getVendorListData(); // fallback → fetch all vendors
    }
  }, [values?.category]);

  useEffect(() => {
    fetchStockList();
    fetchCategories();
  }, []);

  // Table columns
  const purchaseRequestColumns = [
    {
      label: "Item",
      key: "item",
      type: "dropdown",
      options: tableData || [],
    },
    { label: "Unit Price", key: "unitPrice", type: "number" },
    { label: "Quantity", key: "quantity", type: "number" },
    { label: "Amount", key: "amount", type: "number", readOnly: true },
  ];

  // Table row handler
  const handleInputChange = (rowIndex, field, value) => {
    const updatedRows = [...purchaseItemData];
    updatedRows[rowIndex][field] = value;

    if (["unitPrice", "quantity"].includes(field)) {
      const qty = parseFloat(updatedRows[rowIndex].quantity || 0);
      const price = parseFloat(updatedRows[rowIndex].unitPrice || 0);
      updatedRows[rowIndex].amount = qty * price;
    }

    setPurdhaseItemData(updatedRows);
  };

  const openModal = (modalName) => setIsModalOpen(modalName);
  const closeModal = () => setIsModalOpen(null);

  const comprehensiveFields = [
    // General Information
    {
      type: "section_header",
      name: "contact_section",
      label: "General Information",
      colSpan: 12,
      className: "border-b pb-2 mb-2 mt-6 text-xl font-semibold",
    },
    {
      name: "name",
      label: "Vendor Name",
      type: "text",
      required: true,
      colSpan: 3,
    },
    {
      name: "abrv",
      label: "Abbreviation",
      type: "text",
      colSpan: 3,
    },
    {
      name: "tags",
      label: "Tags",
      type: "text",
      required: true,
      colSpan: 3,
    },
    {
      name: "categories",
      label: "Categories",
      type: "select",
      required: true,
      options: category || [],
      multiple: true,
      colSpan: 3,
    },

    // Contact Information
    {
      type: "section_header",
      name: "contact_section",
      label: "Contact Information",
      colSpan: 12,
      className: "border-b pb-2 mb-2 mt-6 text-xl font-semibold",
    },
    {
      name: "phone",
      label: "Phone Number",
      type: "contact",
      required: true,
      countryCodeName: "country_code",
      placeholder: "Enter phone number",
      colSpan: 3,
    },
    {
      name: "phone2",
      label: "Alternate Phone Number",
      type: "contact",
      required: true,
      countryCodeName: "country_code2",
      placeholder: "Enter alternate phone number",
      colSpan: 3,
    },
    {
      name: "email",
      label: "Email",
      type: "email",
      required: true,
      colSpan: 3,
    },

    {
      name: "website",
      label: "Website",
      type: "text",
      colSpan: 3,
    },

    // Location Information
    {
      type: "section_header",
      name: "contact_section",
      label: "Location Information",
      colSpan: 12,
      className: "border-b pb-2 mb-2 mt-6 text-xl font-semibold",
    },
    {
      name: "address",
      label: "Address",
      type: "textarea",
      required: true,
      readOnly: true,
    },
  ];

  const {
    total: totalData,
    currentPage,
    search,
    action,
    handleCreate,
    handleSearchChange,
  } = useCommonFetch();
  const handleVendorSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const payload = { ...values };

      // ✅ Only create flow
      await handleCreate(payload, createVendor, {
        successMessage: "Vendor created successfully!",
        errorMessage: "Failed to create vendor!",
      });

      resetForm();
      navigate("/inventory/vendor");
    } catch (error) {
      console.error("Submit error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {loading ? (
        <Loading />
      ) : (
        <div className="bg-white flex flex-col gap-2">
          <form onSubmit={handleSubmit} className="flex flex-col">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 p-4">
              <DefaultSearchSelectInput
                label="Category"
                name="category"
                options={category}
                onChange={(e) => setFieldValue("category", e)}
                value={values.category}
                error={
                  touched.category && errors.category ? errors.category : ""
                }
              />
              <DefaultSearchSelectInput
                label="Select Vendor"
                classnames="border-white hover:border-borderColor"
                name="vendor"
                options={vendor}
                onChange={(e) => setFieldValue("vendor", e)}
                value={values.vendor}
                error={touched.vendor && errors.vendor ? errors.vendor : ""}
                required
                onAddMore={() => openModal(ModalNames.VENDOR)}
                addmoreClick={true}
              />
              <DefaultDatePicker
                label="Expected Date"
                name="expected_date"
                placeholder="Select"
                value={values.expected_date}
                onChange={(data) => setFieldValue("expected_date", data)}
                onBlur={handleBlur}
                error={
                  touched.expected_date && errors.expected_date
                    ? errors.expected_date
                    : ""
                }
                required
                type="date"
              />
              <DefaultSearchSelectInput
                required
                label="Priority"
                name="priority"
                value={values.priority}
                options={_Priority}
                onChange={(e) => setFieldValue("priority", e)}
                onBlur={handleBlur}
                error={
                  touched.priority && errors.priority ? errors.priority : ""
                }
              />
            </div>

            {/* Dynamic Table */}
            <div className="p-4">
              <DynamicTable
                columns={purchaseRequestColumns}
                rows={purchaseItemData}
                setRows={setPurdhaseItemData}
                error={error}
                handleInputChange={handleInputChange}
              />
            </div>

            {/* textarea */}
            <div className="p-4">
              <DefaultTextarea
                classnames="rounded-md border-0 bg-light"
                name="note"
                label="Description (Optional)"
                placeholder="Write here..."
                boxheight={15}
                value={values.note}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.note && errors.note ? errors.note : ""}
              />
            </div>

            {/* Submit Button */}
            <div className="flex flex-row gap-2 p-4">
              <DefaultButton
                variant="default"
                name={id ? "Update" : "Add"}
                type="submit"
                loading={isSubmitting}
              />
              <DefaultButton
                variant="secondary"
                name="Cancel"
                type="button"
                onClick={() => navigate("/inventory/purchase-order")}
              />
            </div>
          </form>

          {/* Vendor Modal */}
          {isModalOpen === ModalNames.VENDOR && (
            <CommonModal
              modalSize=""
              headerTitle="Add Vendor"
              onClose={closeModal}
              show={isModalOpen === ModalNames.VENDOR}
            >
              <div className="p-4 bg-white rounded-md">
                <CommonForm
                  fields={comprehensiveFields}
                  onSubmit={handleVendorSubmit}
                  submitButtonText="Submit"
                  // onCancel={handleCancel}
                  cancelButtonText="Reset"
                  columnsPerRow={1}
                  gap="gap-4"
                  rowClassName=""
                  fieldClassName="bg-white p-3 rounded shadow-sm"
                />
              </div>
            </CommonModal>
          )}
        </div>
      )}
    </div>
  );
};

export default AddPurchaseOrderForm;
